import { Component } from '@theme/component';

const UPDATE_INTERVAL_MS = 1000;

/**
 * Displays a live countdown to a merchant-configured end date/time.
 * Reads the target date from the `data-end` attribute (format: `YYYY-MM-DD HH:MM`).
 * Hides itself if no valid date has been configured yet.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} units - Wrapper around the days/hours/minutes/seconds values.
 * @property {HTMLElement} [expiredMessage] - Message shown once the countdown reaches zero.
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 *
 * @extends {Component<Refs>}
 */
class CountdownTimerComponent extends Component {
  requiredRefs = ['units'];

  connectedCallback() {
    super.connectedCallback();

    const endValue = this.getAttribute('data-end');
    this.#endTime = endValue ? Date.parse(endValue.replace(' ', 'T')) : NaN;

    if (Number.isNaN(this.#endTime)) {
      // No (valid) end date configured yet — stay hidden until a merchant sets one.
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.#tick();
    this.#interval = setInterval(this.#tick, UPDATE_INTERVAL_MS);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#interval);
  }

  /** @type {number} */
  #endTime = NaN;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval;

  #tick = () => {
    const remainingMs = this.#endTime - Date.now();

    if (remainingMs <= 0) {
      clearInterval(this.#interval);
      this.#setValue('days', 0);
      this.#setValue('hours', 0);
      this.#setValue('minutes', 0);
      this.#setValue('seconds', 0);
      this.#showExpired();
      return;
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#setValue('days', days);
    this.#setValue('hours', hours);
    this.#setValue('minutes', minutes);
    this.#setValue('seconds', seconds);
  };

  /**
   * @param {'days' | 'hours' | 'minutes' | 'seconds'} ref
   * @param {number} value
   */
  #setValue(ref, value) {
    const element = this.refs[ref];
    if (element instanceof HTMLElement) {
      element.textContent = String(value).padStart(2, '0');
    }
  }

  #showExpired() {
    const { units, expiredMessage } = this.refs;
    if (units instanceof HTMLElement) units.hidden = true;
    if (expiredMessage instanceof HTMLElement) expiredMessage.hidden = false;
  }
}

if (!customElements.get('countdown-timer-component')) {
  customElements.define('countdown-timer-component', CountdownTimerComponent);
}
